<template>
  <ValidationObserver ref="observer" tag="form" @submit.prevent="submit">
    <div class="cp-shell">
      <div class="cp-card">
        <div class="cp-grid">
          <div class="cp-field">
            <label class="cp-label">Old password</label>
            <ValidationProvider v-slot="{ errors }" rules="required">
              <div class="cp-input-wrap">
                <input
                    v-model="changePassword.oldPassword"
                    :type="showOldPassword ? 'text' : 'password'"
                    autocomplete="current-password"
                    class="cp-input"
                    name="oldPassword"
                    placeholder="Enter old password"
                />
                <button class="cp-icon-btn" type="button" @click="toggleOldShow">
                  <v-icon small>{{ showOldPassword ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </button>
              </div>
              <span class="cp-error">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="cp-field">
            <label class="cp-label">New password</label>
            <ValidationProvider v-slot="{ errors }" rules="required" vid="confirm">
              <div class="cp-input-wrap">
                <input
                    v-model="changePassword.newPassword"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    class="cp-input"
                    name="newPassword"
                    placeholder="Enter new password"
                />
                <button class="cp-icon-btn" type="button" @click="toggleShow">
                  <v-icon small>{{ showPassword ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </button>
              </div>
              <span class="cp-error">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div class="cp-field">
            <label class="cp-label">Confirm password</label>
            <ValidationProvider v-slot="{ errors }" rules="required|confirmed:confirm">
              <div class="cp-input-wrap">
                <input
                    v-model="confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    class="cp-input"
                    name="confirmPassword"
                    placeholder="Re-enter new password"
                />
                <button class="cp-icon-btn" type="button" @click="toggleConfirmShow">
                  <v-icon small>{{ showConfirmPassword ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
                </button>
              </div>
              <span class="cp-error">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <footer class="cp-actions">
          <v-btn
              :loading="loading"
              class="cp-save-btn"
              color="success"
              type="button"
              @click="submit"
          >
            Save
          </v-btn>
        </footer>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import {extend, ValidationObserver, ValidationProvider} from "vee-validate";
import {confirmed, required} from "vee-validate/dist/rules";
import {LoginService} from "./login.service";

extend("required", {
  ...required,
  message: "This field is required",
});

extend("confirmed", {
  ...confirmed,
  message: "Password doesn't match",
});

export default {
  name: "ChangePassword",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      confirmPassword: null,
      changePassword: {
        oldPassword: null,
        newPassword: null,
      },
      loading: false,
      showPassword: false,
      showConfirmPassword: false,
      showOldPassword: false,
    };
  },
  computed: {
    buttonLabel() {
      return this.showPassword ? "Hide" : "Show";
    },
  },
  methods: {
    toggleShow() {
      this.showPassword = !this.showPassword;
    },
    toggleConfirmShow() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
    toggleOldShow() {
      this.showOldPassword = !this.showOldPassword;
    },
    async submit() {
      this.loading = true;
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        this.loading = false;
        return;
      }

      try {
        const res = await LoginService.changePassword(this.changePassword);
        switch (res.data.status) {
          case 200:
            this.$toast.success(res.data.message);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            this.$router.push("/login");
            break;
          case 500:
            this.$toast.error(res.data.message);
            break;
          default:
            this.$toast.error("Unable to change password, please try again");
        }
      } catch (error) {
        console.error(error);
        this.$toast.error("Unable to change password, please try again");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cp-shell {
  padding: 12px;
  background: #f7f8fa;
}

.cp-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.08);
  padding: 16px 16px 14px;
}

.cp-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.cp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cp-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}

.cp-input-wrap {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px;
}

.cp-input {
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  color: #111827;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.cp-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.15);
  background: #ffffff;
}

.cp-icon-btn {
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #f8fafc;
  color: #1f2937;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.cp-icon-btn:hover {
  background: rgba(14, 165, 233, 0.08);
  border-color: rgba(56, 189, 248, 0.6);
}

.cp-error {
  color: #ef4444;
  font-size: 0.8rem;
  min-height: 18px;
}

.cp-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.cp-save-btn {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.3px;
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(25, 118, 210, 0.25);
}

.cp-save-btn:hover {
  box-shadow: 0 16px 40px rgba(25, 118, 210, 0.32);
}
</style>
